import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Save, ChevronLeft, Info } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import ProfileImageUpload from "./ProfileImageUpload";
import ClientFields from "./ClientFields";
import TherapistFields from "./TherapistFields";
import { saveUserProfile } from "@/utils/userProfileUtils";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

interface ProfileFormProps {
  userData: {
    id: string;
    email: string;
    name: string;
    firstName: string;
    lastName: string;
    role: string;
    profileImageUrl: string | null;
    connectionKey: string;
    websiteUrl: string;
    bookingCalendar: string;
    showActivity: string;
    country: string;
    language: string;
    languageSecondary: string;
    profileBio: string;
  };
  userInitials: string;
}

const languages = [
  "English", "Spanish", "French", "German", "Italian", "Portuguese", 
  "Russian", "Chinese", "Japanese", "Korean", "Arabic", "Hindi", "Other"
];

const priorityCountries = [
  "Australia", "United States of America", "United Kingdom", "Canada", "New Zealand"
];

const otherCountries = [
  "Afghanistan", "Albania", "Algeria", "Andorra", "Angola", "Antigua and Barbuda", "Argentina", "Armenia", 
  "Austria", "Azerbaijan", "Bahamas", "Bahrain", "Bangladesh", "Barbados", "Belarus", "Belgium", "Belize", 
  "Benin", "Bhutan", "Bolivia", "Bosnia and Herzegovina", "Botswana", "Brazil", "Brunei", "Bulgaria", 
  "Burkina Faso", "Burundi", "Cabo Verde", "Cambodia", "Cameroon", "Central African Republic", "Chad", 
  "Chile", "China", "Colombia", "Comoros", "Congo", "Costa Rica", "Croatia", "Cuba", "Cyprus", "Czech Republic", 
  "Denmark", "Djibouti", "Dominica", "Dominican Republic", "Ecuador", "Egypt", "El Salvador", "Equatorial Guinea", 
  "Eritrea", "Estonia", "Eswatini", "Ethiopia", "Fiji", "Finland", "France", "Gabon", "Gambia", "Georgia", 
  "Germany", "Ghana", "Greece", "Grenada", "Guatemala", "Guinea", "Guinea-Bissau", "Guyana", "Haiti", "Honduras", 
  "Hungary", "Iceland", "India", "Indonesia", "Iran", "Iraq", "Ireland", "Israel", "Italy", "Jamaica", "Japan", 
  "Jordan", "Kazakhstan", "Kenya", "Kiribati", "Korea, North", "Korea, South", "Kosovo", "Kuwait", "Kyrgyzstan", 
  "Laos", "Latvia", "Lebanon", "Lesotho", "Liberia", "Libya", "Liechtenstein", "Lithuania", "Luxembourg", "Madagascar", 
  "Malawi", "Malaysia", "Maldives", "Mali", "Malta", "Marshall Islands", "Mauritania", "Mauritius", "Mexico", 
  "Micronesia", "Moldova", "Monaco", "Mongolia", "Montenegro", "Morocco", "Mozambique", "Myanmar", "Namibia", 
  "Nauru", "Nepal", "Netherlands", "Nicaragua", "Niger", "Nigeria", "North Macedonia", "Norway", "Oman", "Pakistan", 
  "Palau", "Palestine", "Panama", "Papua New Guinea", "Paraguay", "Peru", "Philippines", "Poland", "Portugal", 
  "Qatar", "Romania", "Russia", "Rwanda", "Saint Kitts and Nevis", "Saint Lucia", "Saint Vincent and the Grenadines", 
  "Samoa", "San Marino", "Sao Tome and Principe", "Saudi Arabia", "Senegal", "Serbia", "Seychelles", "Sierra Leone", 
  "Singapore", "Slovakia", "Slovenia", "Solomon Islands", "Somalia", "South Africa", "South Sudan", "Spain", 
  "Sri Lanka", "Sudan", "Suriname", "Sweden", "Switzerland", "Syria", "Taiwan", "Tajikistan", "Tanzania", "Thailand", 
  "Timor-Leste", "Togo", "Tonga", "Trinidad and Tobago", "Tunisia", "Turkey", "Turkmenistan", "Tuvalu", "Uganda", 
  "Ukraine", "United Arab Emirates", "Uruguay", "Uzbekistan", "Vanuatu", "Vatican City", "Venezuela", "Vietnam", 
  "Yemen", "Zambia", "Zimbabwe"
];

const countries = [
  ...priorityCountries,
  ...otherCountries.filter(country => !priorityCountries.includes(country))
];

const ProfileForm = ({ userData: initialUserData, userInitials }: ProfileFormProps) => {
  const navigate = useNavigate();
  const [saving, setSaving] = useState(false);
  const [userData, setUserData] = useState({
    ...initialUserData,
    bookingCalendar: initialUserData.bookingCalendar || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      
      // Use the saveUserProfile function from userProfileUtils
      await saveUserProfile(userData);
      
      toast.success("Profile updated successfully");
    } catch (error: any) {
      console.error("Error updating profile:", error);
      toast.error(error.message || "Failed to update profile");
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setUserData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSelectChange = (field: string, value: string) => {
    setUserData(prev => ({
      ...prev,
      [field]: value === "none" ? "" : value
    }));
  };

  const handleRadioChange = (value: string) => {
    setUserData(prev => ({
      ...prev,
      showActivity: value
    }));
  };

  const handleImageUploaded = (imageUrl: string) => {
    setUserData(prev => ({
      ...prev,
      profileImageUrl: imageUrl
    }));
  };

  const goBack = () => {
    if (userData.role === 'therapist') {
      navigate('/therapist');
    } else if (userData.role === 'client') {
      navigate('/journal');
    } else {
      navigate('/auth');
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="bg-white rounded-lg p-6 shadow-md border border-[#CCCCCC]">
        <div className="flex justify-between items-center mb-4">
          {/* Remove this line:
          <h2 className="text-lg font-medium text-[#333B44]">Profile Information</h2>
          */}
        </div>
        
        <ProfileImageUpload 
          userId={userData.id} 
          existingImageUrl={userData.profileImageUrl} 
          userInitials={userInitials} 
          onImageUploaded={handleImageUploaded} 
        />
        
        <div className="space-y-4 mt-6">
          <div className="space-y-2">
            <Label htmlFor="email">Email Address (cannot be changed)</Label>
            <Input 
              id="email" 
              type="email" 
              value={userData.email} 
              readOnly 
              disabled 
              className="bg-[#E8F0F8]" 
            />
          </div>
          
          {userData.role === 'client' && (
            <ClientFields 
              connectionKey={userData.connectionKey}
              showActivity={userData.showActivity}
              onRadioChange={handleRadioChange}
            />
          )}
          
          <div className="space-y-2">
            <Label htmlFor="name">Company {userData.role === 'therapist' ? '' : '(optional)'}</Label>
            <Input 
              id="name" 
              name="name" 
              type="text" 
              placeholder={userData.role === 'therapist' ? "Your company name" : "Company name (optional)"} 
              value={userData.name} 
              onChange={handleInputChange} 
              className="border-[#CCCCCC] focus:border-[#3BAFDA] bg-[#E8F0F8]" 
            />
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input 
                id="firstName" 
                name="firstName" 
                type="text" 
                placeholder="First name" 
                value={userData.firstName} 
                onChange={handleInputChange} 
                className="border-[#CCCCCC] focus:border-[#3BAFDA] bg-[#E8F0F8]" 
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input 
                id="lastName" 
                name="lastName" 
                type="text" 
                placeholder="Last name" 
                value={userData.lastName} 
                onChange={handleInputChange} 
                className="border-[#CCCCCC] focus:border-[#3BAFDA] bg-[#E8F0F8]" 
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Select 
                value={userData.country} 
                onValueChange={(value) => handleSelectChange('country', value)}
              >
                <SelectTrigger className="border-[#CCCCCC] focus:border-[#3BAFDA] bg-[#E8F0F8]">
                  <SelectValue placeholder="Select your country" />
                </SelectTrigger>
                <SelectContent className="max-h-80">
                  {countries.map(country => (
                    <SelectItem key={country} value={country}>{country}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="language">Primary Language</Label>
              <Select 
                value={userData.language} 
                onValueChange={(value) => handleSelectChange('language', value)}
              >
                <SelectTrigger className="border-[#CCCCCC] focus:border-[#3BAFDA] bg-[#E8F0F8]">
                  <SelectValue placeholder="Select your language" />
                </SelectTrigger>
                <SelectContent>
                  {languages.map(language => (
                    <SelectItem key={language} value={language}>{language}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="languageSecondary">Secondary Language (optional)</Label>
            <Select 
              value={userData.languageSecondary || "none"} 
              onValueChange={(value) => handleSelectChange('languageSecondary', value)}
            >
              <SelectTrigger className="border-[#CCCCCC] focus:border-[#3BAFDA] bg-[#E8F0F8]">
                <SelectValue placeholder="Select a secondary language" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">None</SelectItem>
                {languages
                  .filter(lang => lang !== userData.language)
                  .map(language => (
                    <SelectItem key={language} value={language}>{language}</SelectItem>
                  ))
                }
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Select a second language if you are multilingual
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="profileBio">Profile Bio</Label>
            <Textarea 
              id="profileBio" 
              name="profileBio" 
              placeholder="Tell us about yourself (max 2000 characters)" 
              value={userData.profileBio} 
              onChange={handleInputChange} 
              className="border-[#CCCCCC] focus:border-[#3BAFDA] min-h-[120px] bg-[#E8F0F8]" 
              maxLength={2000}
            />
            <p className="text-xs text-right text-muted-foreground">
              {userData.profileBio.length}/2000 characters
            </p>
          </div>

          {userData.role === 'therapist' && (
            <TherapistFields 
              websiteUrl={userData.websiteUrl}
              bookingCalendarUrl={userData.bookingCalendar}
              onChange={handleInputChange} 
            />
          )}
        </div>
      </div>
      
      <div className="pt-4 flex justify-between">
        <span />
        <Button 
          type="submit" 
          disabled={saving} 
          className="bg-gradient-to-r from-[#3BAFDA] to-[#333B44] hover:from-[#333B44] hover:to-[#333B44] text-white"
        >
          {saving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2 h-4 w-4" />
              Save Profile
            </>
          )}
        </Button>
      </div>
    </form>
  );
};

export default ProfileForm;
